import {ReactNode, useCallback, useMemo} from "react";
import ManageKeysPage from '../pages/manage-keys/ManageKeyPage';
import KeyVaultsPage from '../pages/key-vaults/KeyVaultsPage';
import KeyTagsPage from '../pages/key-tags/KeyTagsPage';
import SeatingPlanListPage from '../pages/seating-plan-list-page/SeatingPlanListPage';
import SeatingPlanPage from '../pages/seating-plan-page/SeatingPlanPage';
import UserRolesPage from '../pages/roles/UserRolesPage';
import VisitReportPage from '../pages/visit-report/VisitReportPage';
import EmployeeVisitReportPage from '../pages/employee-visit-report/EmployeeVisitReportPage';
import ContractorVisitReportPage from '../pages/contractor-visit-report/ContractorVisitReportPage';
import NeighbourhoodListPage from '../pages/neighbourhood-list/NeighbourhoodListPage';
import NeighbourhoodSnapshotsPage from '../pages/neighbourhood-snapshots/NeighbourhoodSnapshotsPage';
import CompanyTreemapPage from '../pages/company-treemap/CompanyTreemapPage';
import HibobResidencePage from "../pages/hibob-residence-page/HibobResidencePage";
import OfficesPage from "../pages/offices-page/OfficesPage";
import SlackSettingsPage from "../pages/slack-settings/SlackSettingsPage";
import LoggersPage from "../pages/loggers/LoggersPage";
import KeyIcon from "@mui/icons-material/Key";
import KeyVaultIcon from "@mui/icons-material/AccountBalance";
import KeyTagIcon from "@mui/icons-material/Label";
import NeighbourhoodIcon from '@mui/icons-material/Cottage';
import RoleIcon from "@mui/icons-material/TheaterComedy";
import SeatingPlanIcon from '@mui/icons-material/Deck';
import SnapshotIcon from '@mui/icons-material/PhotoCamera';
import DeskIcon from '@mui/icons-material/EventSeat';
import FloorIcon from '@mui/icons-material/Layers';
import EmployeeIcon from "@mui/icons-material/People";
import VisitReportIcon from "@mui/icons-material/Assessment";
import OfficeIcon from '@mui/icons-material/Apartment';
import HibobResidenceIcon from '@mui/icons-material/Public';
import TreeIcon from '@mui/icons-material/AccountTree';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardPage from "../pages/dashboard/DashboardPage";
import IssueKeyPage from "../pages/issue-key/IssueKeyPage";
import {CollectKeyPage} from "../pages/collect-keys/CollectKeyPage";
import {MoveKeysPage} from "../pages/move-keys/MoveKeysPage";
import {KeyOperationsPage} from "../pages/key-operations/KeyOperationsPage";
import KeyOperationsIcon from '@mui/icons-material/SwapHoriz';
import UpdateIcon from '@mui/icons-material/Update';
import EmployeesPage from "../pages/employees/EmployeesPage";
import EmployeePortraitListPage from "../pages/employee-portrait-list/EmployeePortraitListPage";
import {DefaultKeyVaultProvider, FilterStoreProvider} from "../components/loading-data/LoadStore";
import {NewJoinersPage} from '../pages/new-joiners/NewJoinersPage';
import NewJoinersIcon from '@mui/icons-material/PersonAdd';
import SlackIcon from "../components/icons/SlackIcon";
import TokenIcon from '@mui/icons-material/VpnKey';
import LoggerIcon from '@mui/icons-material/Terminal';
import {useCurrentOfficeStore} from "../api/CurrentOfficeStore";
import Permission, {
    ClientLoggers_Admin,
    Desks_Read,
    Employees_Admin,
    Employees_Read,
    Floors_Read,
    KeyData_Admin,
    KeyData_Operate,
    KeyData_Read,
    KeyTags_Admin,
    KeyVaults_Admin,
    KeyVaults_Read,
    Neighbourhoods_Read,
    NewJoiners_Read,
    Offices_Admin,
    PermanentTokens_Read,
    SeatingPlans_Read,
    SlackSettings_Read,
    UserRoles_Read,
    VisitReports_Read,
    WorkSites_Admin
} from "../util/access/Permission";
import PermanentTokenPage from "../pages/permanent-tokens/PermanentTokenPage";
import {KeyLastUpdatesPage} from "../pages/key-last-updates/KeyLastUpdatesPage";
import {useAccess} from "../util/access/UseAccess";
import DesksPage from "../pages/desks/DesksPage";
import FloorsPage from "../pages/floors/FloorsPage";
import FloorPage from "../pages/floors/FloorPage";
import {Access} from "../util/access/Access";
import {toErrorPageProps} from "../components/error/ErrorPageBoundary";
import {ErrorPage} from "../components/error/ErrorPage";
import {NavigationError} from "../components/error/NavigationError";


type AppPageNavigation = {
    type: 'sidebar',
    section: string,
    label: string,
    icon?: ReactNode,
} | {
    type: 'hidden',
}

function sidebarNavigation(section: string, label: string, icon?: ReactNode) {
    return {type: 'sidebar', section, label, icon};
}

const hiddenNavigation = {type: 'hidden'};

export interface AppPage<N extends AppPageNavigation> {
    path: string;
    component: ReactNode | ((access: Access) => ReactNode),
    navigation: N,
    requiredPermissions: readonly Permission[],
    requiredJetBrains?: boolean,
}

export interface AppNavigation {
    siteLogo: AppPage<any>;
    sidebar: {
        section: string,
        pages: AppPage<{ type: 'sidebar', section: string, label: string, icon?: ReactNode }>[]
    }[];
}

/**
 * Object containing all route paths in the application
 */
export const AppPages: Record<string, AppPage<any>> = {
    // Main routes
    home: {
        path: '/',
        component: access => {
            const maxRole = access.maxRole().type;
            switch (maxRole) {
                case "guest":
                    return <ErrorPage {...toErrorPageProps(NavigationError.unauthorized())}/>;
                case 'vault-manager':
                    return <KeyOperationsPage/>;
                case "office-admin":
                case "system-admin":
                    return <DashboardPage/>;
            }
        },
        navigation: hiddenNavigation,
        requiredPermissions: [],
    },
    dashboard: {
        path: '/dashboard',
        component: <DashboardPage/>,
        navigation: sidebarNavigation('', 'Dashboard', <DashboardIcon/>),
        requiredPermissions: [KeyData_Read],
        requiredJetBrains: true,
    },
    issueKey: {
        path: '/issue-key',
        component: (
            <DefaultKeyVaultProvider>
                {defaultKeyVault => <IssueKeyPage defaultKeyVault={defaultKeyVault}/>}
            </DefaultKeyVaultProvider>
        ),
        navigation: hiddenNavigation,
        requiredPermissions: [KeyData_Read, KeyData_Operate],
    },
    returnKey: {
        path: '/collect-keys',
        component: (
            <FilterStoreProvider>
                {(filterStores) => <CollectKeyPage {...filterStores}/>}
            </FilterStoreProvider>
        ),
        navigation: hiddenNavigation,
        requiredPermissions: [KeyData_Read, KeyData_Operate],
    },
    moveKeys: {
        path: '/move-keys',
        component: (
            <FilterStoreProvider>
                {(filterStores) => <MoveKeysPage {...filterStores}/>}
            </FilterStoreProvider>
        ),
        navigation: hiddenNavigation,
        requiredPermissions: [KeyData_Admin, KeyVaults_Read],
    },
    keyTransactions: {
        path: '/keys',
        component: (
            <KeyOperationsPage/>
        ),
        navigation: sidebarNavigation("Keys", "Key Operations", <KeyOperationsIcon/>),
        requiredPermissions: [KeyData_Read, KeyData_Operate],
    },
    keyLastUpdates: {
        path: '/key-last-updates',
        component: (
            <FilterStoreProvider>
                {(filterStores) => <KeyLastUpdatesPage {...filterStores}/>}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Keys', 'Last Updates', <UpdateIcon/>),
        requiredPermissions: [KeyData_Read],
        requiredJetBrains: true,
    },
    keys: {
        path: '/manage-keys',
        component: (
            <FilterStoreProvider>
                {(filterStores) => <ManageKeysPage {...filterStores}/>}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Keys', 'Manage Keys', <KeyIcon/>),
        requiredPermissions: [KeyData_Operate],
        requiredJetBrains: true,
    },
    employees: {
        path: '/employees',
        component: (
            <FilterStoreProvider>
                {(filterStores) => <EmployeesPage {...filterStores}/>}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Employees', 'Employees', <EmployeeIcon/>),
        requiredPermissions: [Employees_Read, Neighbourhoods_Read, Desks_Read],
        requiredJetBrains: true,
    },
    employeeVisitReport: {
        path: '/employee-visit-report',
        component: (
            <FilterStoreProvider>
                {(filterStores) => (
                    <EmployeeVisitReportPage {...filterStores}/>
                )}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Employees', 'Employee Visits', <VisitReportIcon/>),
        requiredPermissions: [Employees_Admin, VisitReports_Read],
    },
    contractorVisitReport: {
        path: '/contractor-visit-report',
        component: <ContractorVisitReportPage/>,
        navigation: sidebarNavigation('Employees', 'Contractor Visits', <VisitReportIcon/>),
        requiredPermissions: [KeyData_Read, VisitReports_Read],
    },
    employeePortraits: {
        path: '/employees-portraits',
        component: (
            <FilterStoreProvider>
                {filterStores => (
                    <EmployeePortraitListPage {...filterStores}/>
                )}
            </FilterStoreProvider>
        ),
        navigation: hiddenNavigation,
        requiredPermissions: [Employees_Admin],
    },
    employeeTreemapReport: {
        path: '/company-treemap',
        component: (
            <FilterStoreProvider>
                {filterStores => (
                    <CompanyTreemapPage {...filterStores}/>
                )}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Employees', 'Company Treemap', <TreeIcon/>),
        requiredPermissions: [Employees_Read, Neighbourhoods_Read],
    },
    neighbourhoodVisitReport: {
        path: '/neighbourhoods',
        component: <NeighbourhoodListPage/>,
        navigation: sidebarNavigation('Neighbourhoods', 'Neighbourhoods', <NeighbourhoodIcon/>),
        requiredPermissions: [Neighbourhoods_Read, VisitReports_Read, Desks_Read],
    },
    floors: {
        path: '/floors',
        component: <FloorsPage/>,
        navigation: sidebarNavigation('Neighbourhoods', 'Floors', <FloorIcon/>),
        requiredPermissions: [Floors_Read, Desks_Read, Neighbourhoods_Read],
    },
    floor: {
        path: '/floors/:floorId',
        component: <FloorPage/>,
        navigation: hiddenNavigation,
        requiredPermissions: [Floors_Read, Desks_Read, Neighbourhoods_Read],
    },
    desks: {
        path: '/desks',
        component: <DesksPage/>,
        navigation: sidebarNavigation('Neighbourhoods', 'Desks', <DeskIcon/>),
        requiredPermissions: [Desks_Read, Floors_Read, Neighbourhoods_Read],
    },
    seatingPlanList: {
        path: '/seating-plans',
        component: <SeatingPlanListPage/>,
        navigation: sidebarNavigation('Neighbourhoods', 'Seating Plans', <SeatingPlanIcon/>),
        requiredPermissions: [SeatingPlans_Read],
    },
    neighbourhoodSnapshots: {
        path: '/neighbourhood-snapshots',
        component: <NeighbourhoodSnapshotsPage/>,
        navigation: sidebarNavigation('Neighbourhoods', 'Snapshots', <SnapshotIcon/>),
        requiredPermissions: [SeatingPlans_Read],
    },
    newJoiners: {
        path: '/new-joiners',
        component: (
            <FilterStoreProvider>
                {filterStores => (
                    <NewJoinersPage {...filterStores}/>
                )}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Employees', 'New Joiners', <NewJoinersIcon/>),
        requiredPermissions: [NewJoiners_Read],
        requiredJetBrains: true,
    },
    seatingPlan: {
        path: '/seating-plans/:planId',
        component: <SeatingPlanPage/>,
        navigation: hiddenNavigation,
        requiredPermissions: [SeatingPlans_Read],
    },
    visitReports: {
        path: '/upload-visit-reports',
        component: <VisitReportPage/>,
        navigation: sidebarNavigation('Office Administration', 'Visit Reports', <VisitReportIcon/>),
        requiredPermissions: [VisitReports_Read],
    },
    keyVaults: {
        path: '/key-vaults',
        component: <KeyVaultsPage/>,
        navigation: sidebarNavigation('Office Administration', 'Key Vaults', <KeyVaultIcon/>),
        requiredPermissions: [KeyVaults_Admin],
    },
    hibobResidence: {
        path: '/hibob-residence',
        component: <HibobResidencePage/>,
        navigation: sidebarNavigation('Global Administration', 'Hibob Residence', <HibobResidenceIcon/>),
        requiredPermissions: [WorkSites_Admin],
    },
    offices: {
        path: '/offices',
        component: <OfficesPage/>,
        navigation: sidebarNavigation('Global Administration', 'Offices', <OfficeIcon/>),
        requiredPermissions: [Offices_Admin],
    },
    keyTags: {
        path: '/key-tags',
        component: <KeyTagsPage/>,
        navigation: sidebarNavigation('Office Administration', 'Key Tags', <KeyTagIcon/>),
        requiredPermissions: [KeyTags_Admin],
    },
    roles: {
        path: '/roles',
        component: (
            <FilterStoreProvider>
                {filterStores => (
                    <UserRolesPage {...filterStores}/>
                )}
            </FilterStoreProvider>
        ),
        navigation: sidebarNavigation('Global Administration', 'User Roles', <RoleIcon/>),
        requiredPermissions: [UserRoles_Read],
    },
    permanentTokens: {
        path: '/permanent-tokens',
        component: <PermanentTokenPage/>,
        navigation: sidebarNavigation('Global Administration', 'Permanent Tokens', <TokenIcon/>),
        requiredPermissions: [PermanentTokens_Read],
    },
    slackSettings: {
        path: '/slack-settings',
        component: <SlackSettingsPage/>,
        navigation: sidebarNavigation('Office Administration', 'Slack Settings', <SlackIcon/>),
        requiredPermissions: [SlackSettings_Read],
    },
    loggers: {
        path: '/loggers',
        component: <LoggersPage/>,
        navigation: sidebarNavigation('Global Administration', 'Loggers', <LoggerIcon/>),
        requiredPermissions: [ClientLoggers_Admin],
    },
} as const;

export function useAppNavigation(): AppNavigation {
    const access = useAccess();

    return useMemo<AppNavigation>(
        () => {
            const allPages = Object.values(AppPages).filter(page => access.has(page.requiredPermissions) && access.matchesJetBrains(page.requiredJetBrains));

            const sidebarPages = allPages.filter(page => page.navigation.type === 'sidebar');
            const sidebar = Object.entries(
                sidebarPages.reduce((acc, page) => {
                    const section = page.navigation.section;
                    if (!acc[section]) {
                        acc[section] = [];
                    }
                    acc[section].push(page);
                    return acc;
                }, {} as Record<string, typeof sidebarPages>)
            ).map(([section, pages]) => ({
                section,
                pages
            }));

            return {
                siteLogo: AppPages.home,
                sidebar,
            };
        },
        [access]
    );
}

const placeholderPattern = /:(\w+)\b/g;

export function pageUrl(
    officePathElement: String,
    page: AppPage<any>,
    pathParameters: Record<string, any> = {},
    queryParameters: Record<string, any> = {}
): string {
    const basePath = '/offices/' + officePathElement + page.path.replaceAll(placeholderPattern, substring =>
        pathParameters[substring.substring(1)]?.toString()
    );

    const queryString = Object.entries(queryParameters)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

    return queryString ? `${basePath}?${queryString}` : basePath;
}

export function usePageUrl(safe: boolean = false): (page: AppPage<any>, pathParameters?: Record<string, any>, queryParameters?: Record<string, any>) => string {
    const access = useAccess();
    const officePathElement = useCurrentOfficeStore(state => state.officePathElement);

    return useCallback(
        (page, pathParameters = {}, queryParameters = {}) => {
            const actualPathElement = officePathElement || access.accessibleOffices.getDefaultOfficePath();
            if (!actualPathElement) {
                if (safe) {
                    return '';
                } else {
                    throw new Error("Cannot generate URL for page without office path element in context");
                }
            }
            return pageUrl(actualPathElement, page, pathParameters, queryParameters);
        },
        [safe, access, officePathElement],
    );
}
